import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, TextField, Button, Stack, Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress, Divider, Chip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { getSettings, updateSettings } from '../../api/endpoints/settings';

const WHATSAPP_KEYS = [
  'whatsappProvider',
  'whatsappInstanceId',
  'whatsappAccessToken',
  'whatsappApiUrl',
  'whatsappSourceNumber',
];

const SMS_KEYS = [
  'smsProvider',
  'msg91AuthKey',
  'smsSenderId',
  'smsOtpFlowId',
];

const EMAIL_KEYS = [
  'smtpHost',
  'smtpPort',
  'smtpSecure',
  'smtpUser',
  'smtpPass',
  'smtpFrom',
];

const IntegrationSettings = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await getSettings();
      setForm(res.data.data || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allKeys = [...WHATSAPP_KEYS, ...SMS_KEYS, ...EMAIL_KEYS];
      for (const key of allKeys) {
        if (form[key] !== undefined) {
          await updateSettings(key, form[key]);
        }
      }
      toast.success('Integration settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Integration Settings" subtitle="Configure WhatsApp and Email integrations" />
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Typography variant="h6" fontWeight={700}>WhatsApp</Typography>
              <Chip label="K_API / Twilio / MSG91 / Gupshup" size="small" color="primary" variant="outlined" />
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select value={form.whatsappProvider || ''} label="Provider" onChange={handleChange('whatsappProvider')}>
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="k_api">K API (k.api.io.in)</MenuItem>
                    <MenuItem value="twilio">Twilio</MenuItem>
                    <MenuItem value="msg91">MSG91</MenuItem>
                    <MenuItem value="gupshup">Gupshup</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="WhatsApp API URL" value={form.whatsappApiUrl || ''} onChange={handleChange('whatsappApiUrl')} placeholder="https://k.api.io.in/api/send" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Instance ID" value={form.whatsappInstanceId || ''} onChange={handleChange('whatsappInstanceId')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Access Token" type="password" value={form.whatsappAccessToken || ''} onChange={handleChange('whatsappAccessToken')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Source Number (with country code)" value={form.whatsappSourceNumber || ''} onChange={handleChange('whatsappSourceNumber')} placeholder="+919876543210" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Typography variant="h6" fontWeight={700}>SMS</Typography>
              <Chip label="MSG91" size="small" color="primary" variant="outlined" />
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select value={form.smsProvider || ''} label="Provider" onChange={handleChange('smsProvider')}>
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="msg91">MSG91</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="MSG91 Auth Key" type="password" value={form.msg91AuthKey || ''} onChange={handleChange('msg91AuthKey')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Sender ID" value={form.smsSenderId || ''} onChange={handleChange('smsSenderId')} placeholder="ASTROP" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="OTP Flow ID" value={form.smsOtpFlowId || ''} onChange={handleChange('smsOtpFlowId')} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={2}>Email (SMTP)</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="SMTP Host" value={form.smtpHost || ''} onChange={handleChange('smtpHost')} placeholder="smtp.gmail.com" />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="Port" type="number" value={form.smtpPort || ''} onChange={handleChange('smtpPort')} placeholder="587" />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Secure</InputLabel>
                  <Select value={form.smtpSecure || 'false'} label="Secure" onChange={handleChange('smtpSecure')}>
                    <MenuItem value="true">SSL (465)</MenuItem>
                    <MenuItem value="false">TLS (587)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="SMTP User" value={form.smtpUser || ''} onChange={handleChange('smtpUser')} placeholder="your@email.com" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="SMTP Password" type="password" value={form.smtpPass || ''} onChange={handleChange('smtpPass')} placeholder="App password" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="From Address" value={form.smtpFrom || ''} onChange={handleChange('smtpFrom')} placeholder="noreply@allso.in" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />} disabled={saving}>
            {saving ? 'Saving...' : 'Save Integration Settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default IntegrationSettings;